import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  message,
  PageHeader,
  Select,
  Form,
  Row,
  Col,
  Table,
  InputNumber,
  Popconfirm,
  Typography,
  Spin,
  Tooltip,
} from "antd";
import Page from "_components/Page";
import {
  productForecastService,
  currencyService,
  materialTypeService,
} from "_services";
import { CurrencyFormatter } from "_helpers/formatters";

function Add({ history }) {
  const [form] = Form.useForm();
  const { Option } = Select;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [stockClassificationTypeId, setStockClassificationTypeId] = useState();
  const [finishedProducts, setFinishedProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState();
  const [currencies, setCurrencies] = useState([]);
  const [materialData, setMaterialData] = useState([]);
  const [forecastValue, setForecastValue] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState("");

  const onCancel = () => {
    history.push("/dashboard");
  };

  const getFinishedProducts = () => {
    materialTypeService
      .getAllFinished()
      .then((x) => {
        setFinishedProducts(x.content || []);
      })
      .catch((error) => {
        setError(`${error.message ?? "Please fix errors"}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getCurrencies = () => {
    setIsLoading(true);
    currencyService
      .getSuggestion()
      .then((x) => {
        setCurrencies(x.content);
      })
      .catch((error) => {
        setError(`${error.message ?? "Please fix errors"}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onSelectProduct = (productId) => {
    setSelectedProductId(productId);
  };

  const onSelectCurrency = (currencyId) => {
    const newSelectedCurrency = currencies.find((singleCurrency) => {
      return singleCurrency.id === currencyId;
    });
    setSelectedCurrency(newSelectedCurrency.name);
  };

  const getProductMaterials = () => {
    const selectedProduct = finishedProducts.find((singleProduct) => {
      return singleProduct.id === selectedProductId;
    });
    if (selectedProduct?.materials) {
      const modifiedMaterial = selectedProduct.materials.map(
        (singleMaterial) => {
          return {
            id: singleMaterial.materialType?.id,
            itemDescription: singleMaterial.materialType?.name ?? "",
            hsCode: singleMaterial.materialType?.code ?? "",
            secondaryCode: singleMaterial.materialType?.secondaryCode ?? "",
            quantityPerUnit: singleMaterial.quantityPerUnit ?? 0,
            uom: singleMaterial.uom.name ?? "",
            conversionRatio: singleMaterial.conversionRatio ?? 0,
            wastagePercentage: singleMaterial.wastagePercentage ?? 0,
            totalQuantityWithoutWastage: 0,
            totalQuantityIncludingWastage: 0,
            totalWastage: 0,
            unitPrice: 0,
            totalLkr: 0,
            totalUsd: 0,
          };
        }
      );
      setMaterialData(modifiedMaterial);
    }
  };

  const onForecastChangeValues = () => {
    if (materialData) {
      const modifiedMaterialData = materialData.map((singleData) => {
        return {
          ...singleData,
          totalQuantityIncludingWastage:
            singleData.quantityPerUnit * forecastValue,
          totalQuantityWithoutWastage:
            singleData.quantityPerUnit * forecastValue -
            singleData.quantityPerUnit *
              forecastValue *
              (singleData.wastagePercentage / 100),
          totalWastage:
            singleData.quantityPerUnit *
            forecastValue *
            (singleData.wastagePercentage / 100),
          totalLkr: singleData.unitPrice * forecastValue,
          totalUsd: singleData.unitPrice * forecastValue * exchangeRate,
        };
      });
      setMaterialData(modifiedMaterialData);
    }
  };

  const handleUnitPriceChange = (unitPrice, materialId) => {
    const modifiedMaterialData = materialData.map((singleData) => {
      if (singleData.id === materialId) {
        return {
          ...singleData,
          unitPrice,
          totalLkr: singleData.totalQuantityIncludingWastage * unitPrice,
          totalUsd:
            singleData.totalQuantityIncludingWastage * unitPrice * exchangeRate,
        };
      } else {
        return singleData;
      }
    });
    setMaterialData(modifiedMaterialData);
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const modifiedMaterialData = materialData.map((singleData) => {
          return {
            rawMaterial: { id: singleData.id },
            unitPrice: singleData.unitPrice,
          };
        });
        const payload = {
          product: values.product,
          forecastQuantity: values.forecastValue,
          currency: values.currency,
          exchangeRate: values.exchangeRate,
          rawMaterials: modifiedMaterialData,
        };

        setIsSaving(true);
        productForecastService
          .create(payload)
          .then((x) => {
            setIsSaving(false);
            message.success("Successfully created!");
            form.resetFields();
            setMaterialData([]);
          })
          .catch((error) => {
            setIsSaving(false);
            setError(error);
          });
      })
      .catch((error) => {
        //setError(`${error.message ?? "Please fix errors"}`);
      });
    /*       .finally(() => {
        setIsLoading(false);
      });
 */
  };

  const getActionButtons = () => {
    let buttons = [];
    buttons.push(
      <Popconfirm
        title="Are you sure to save product forecast?"
        onConfirm={handleFormSubmit}
        key="add-confirm"
      >
        <Button
          key="1"
          type="primary"
          htmlType="submit"
          loading={isSaving}
          disabled={isSaving}
        >
          Save
        </Button>
      </Popconfirm>
    );
    buttons.push(
      <Button key="2" htmlType="button" onClick={onCancel}>
        Cancel
      </Button>
    );

    return buttons;
  };

  useEffect(() => {
    getFinishedProducts();
  }, []);

  //reset values if the selected product changes
  useEffect(() => {
    if (selectedProductId) {
      getProductMaterials();
      setForecastValue(0);
      setExchangeRate(0);
      //reset form data into intial values
      form.setFieldValue("forecastValue", 0);
      form.setFieldValue("exchangeRate", 0);
      if (currencies.length <= 0) {
        getCurrencies();
      }
    }
  }, [selectedProductId]);

  useEffect(() => {
    if (forecastValue && materialData) {
      onForecastChangeValues();
    }
  }, [forecastValue, exchangeRate]);

  const materialTableColumns = [
    {
      title: "Item Description",
      dataIndex: "itemDescription",
      key: "itemDescription",
      width: "20%",
      render: (text, record) => record.itemDescription,
    },
    {
      title: "HS Code",
      dataIndex: "hsCode",
      key: "hsCode",
      width: "8%",
      render: (text, record) => record.hsCode ?? "",
    },
    {
      title: "Company Code",
      dataIndex: "secondaryCode",
      key: "secondaryCode",
      width: "8%",
      // render: (text, record) => {
      //   return (
      //     record?.secondaryCode.toString().substring(0, 16) +
      //     record?.secondaryCode.toString().slice(-5)
      //   );
      // },

      render: (text, record) => (
        <Tooltip title={text}>{text?.substring(0, 8)}</Tooltip>
      ),
    },
    {
      title: "Quantity Per Unit",
      dataIndex: "quantityPerUnit",
      key: "quantityPerUnit",
      width: "8%",
      render: (text, record) => record.quantityPerUnit ?? 0,
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: "5%",
      render: (text, record) => record.uom ?? "",
    },
    {
      title: "Conversion Ratio",
      dataIndex: "conversionRatio",
      key: "conversionRatio",
      width: "8%",
      render: (text, record) => record.conversionRatio ?? 0,
    },
    {
      title: "Wastage%",
      dataIndex: "wastage",
      key: "wastage",
      width: "2%",
      render: (text, record) => record.wastagePercentage ?? 0,
    },
    {
      title: "Total Wastage",
      dataIndex: "totalWastage ",
      key: "totalWastage",
      width: "8%",
      render: (text, record) => Number(record.totalWastage).toFixed(2) ?? 0,
    },
    {
      title: "Total Qty without Wastage",
      dataIndex: "totalQuantityWithoutWastage ",
      key: "totalQuantityWithoutWastage",
      width: "8%",
      render: (text, record) =>
        Number(record.totalQuantityWithoutWastage).toFixed(2) ?? 0,
    },
    {
      title: "Total Qty with Wastage",
      dataIndex: "totalQuantityIncludingWastage ",
      key: "totalQuantityIncludingWastage",
      width: "8%",
      render: (text, record) =>
        Number(record.totalQuantityIncludingWastage).toFixed(2) ?? 0,
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice ",
      key: "unitPrice",
      width: "8%",
      render: (text, record) => {
        return (
          <InputNumber
            style={{ width: "100%" }}
            controls={false}
            placeholder="Enter unit price"
            defaultValue={record.unitPrice}
            onChange={(e) => handleUnitPriceChange(e, record.id)}
          />
        );
      },
    },
    {
      title: `Total Import Price(${selectedCurrency})`,
      dataIndex: "totalLkr ",
      key: "totalLkr",
      width: "8%",
      render: (text, record) => Number(record.totalLkr).toFixed(2) ?? 0,
    },
    {
      title: "Total Import Price(LKR)",
      dataIndex: "totalUsd ",
      key: "totalUsd",
      width: "8%",
      render: (text, record) => Number(record.totalUsd).toFixed(2) ?? 0,
    },
  ];

  return (
    <div>
      <PageHeader
        title={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/dashboard">Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/bom/boms">BOM</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/bom/add">BOM Calculator</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={getActionButtons()}
      >
        <Page inner error={error}>
          <Spin tip="Loading..." spinning={isLoading}>
            <Form
              layout="vertical"
              //use form instance from a hook
              form={form}
              name="form-create"
              requiredMark={false}
            >
              {finishedProducts.length > 0 && (
                // uses 24 col grid
                <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item
                      name={["product", "id"]}
                      label="Finished Good"
                      rules={[
                        { required: true, message: "Product is required" },
                      ]}
                    >
                      <Select
                        placeholder="Please select"
                        onChange={onSelectProduct}
                        allowClear
                      >
                        {finishedProducts.length > 0 &&
                          finishedProducts.map((t) => (
                            <Option value={t.id} key={t.id}>
                              {t.name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item
                      name={"forecastValue"}
                      label="Forecast Quantity"
                      rules={[
                        {
                          required: true,
                          message: "Forecast Quantity is required",
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        controls={false}
                        placeholder="Enter forecast value"
                        defaultValue={forecastValue}
                        onChange={(value) => {
                          setForecastValue(value);
                        }}
                        // onChange={(value)=>console.log("vv",value)}
                      />
                    </Form.Item>
                  </Col>

                  {currencies.length > 0 && materialData.length > 0 && (
                    <>
                      <Col span={6}>
                        <Form.Item
                          name={["currency", "id"]}
                          label="Currency"
                          rules={[
                            { required: true, message: "Currency is required" },
                          ]}
                        >
                          <Select
                            placeholder="Please select"
                            onChange={onSelectCurrency}
                            allowClear
                          >
                            {currencies.length > 0 &&
                              currencies
                                .filter((item) => item.name !== "LKR")
                                .map((t) => (
                                  <Option value={t.id} key={t.id}>
                                    {t.name}
                                  </Option>
                                ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col span={6}>
                        <Form.Item
                          name={"exchangeRate"}
                          label="Exchange Rate"
                          rules={[
                            {
                              required: true,
                              message: "Exchange rate is required",
                            },
                          ]}
                        >
                          <InputNumber
                            style={{ width: "100%" }}
                            controls={false}
                            placeholder="Enter exchange rate"
                            defaultValue={exchangeRate}
                            onChange={(value) => {
                              setExchangeRate(value);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}

                  {materialData.length > 0 && (
                    <Table
                      bordered
                      pagination={false}
                      columns={materialTableColumns}
                      dataSource={materialData}
                      size="small"
                      summary={(pageData) => {
                        let totalUsdSum = 0;
                        let totalLkrSum = 0;

                        pageData.forEach(({ totalUsd, totalLkr }) => {
                          totalUsdSum += totalUsd;
                          totalLkrSum += totalLkr;
                        });

                        return (
                          <>
                            <Table.Summary.Row>
                              <Table.Summary.Cell colSpan={11}>
                                <Typography.Text strong>Total</Typography.Text>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell align="right">
                                <Typography.Text strong>
                                  {CurrencyFormatter(totalLkrSum)}
                                </Typography.Text>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell align="right">
                                <Typography.Text strong>
                                  {CurrencyFormatter(totalUsdSum)}
                                </Typography.Text>
                              </Table.Summary.Cell>
                            </Table.Summary.Row>
                          </>
                        );
                      }}
                    />
                  )}
                </Row>
              )}
            </Form>
          </Spin>
        </Page>
      </PageHeader>
    </div>
  );
}

export default Add;
